/**
 * "Attach from Files" (spec §5.8.5): recent files, or a title search with a
 * short debounce; pick several and attach them. Mochi reads them with the
 * person's own access, so only files they can open are listed.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, FileText, Loader2, Search, X } from "lucide-react";

import { driveService, type DriveItem } from "../../../modules/vault/api";
import "../fm/fm.css";

export interface PickedFile {
  id: string;
  name: string;
  size: number;
}

/** Real uploaded files only — folders and Write/Grid/Present mirrors are not
 * readable as attachments. */
const attachable = (items: DriveItem[]) =>
  items.filter((i) => i.type === "file" && i.external_service !== "docs" && !i.deleted_at);

export function FilesPicker({
  max,
  onPick,
  onClose,
}: {
  max: number;
  onPick: (files: PickedFile[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<DriveItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState<Map<string, PickedFile>>(new Map());
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let live = true;
    const q = query.trim();
    const timer = setTimeout(
      () => {
        setFailed(false);
        (q ? driveService.searchItems(q, 30) : driveService.getRecents(30))
          .then((found) => live && setItems(attachable(found)))
          .catch(() => {
            if (live) {
              setItems([]);
              setFailed(true);
            }
          });
      },
      q ? 200 : 0,
    );
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  const toggle = (item: DriveItem) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else if (next.size < max) next.set(item.id, { id: item.id, name: item.name, size: item.size });
      return next;
    });

  const title = t("mochi.create.picker.title", "Attach from Files");
  return createPortal(
    <div
      className="fm fixed inset-0 z-[9700] grid place-items-center p-4"
      data-fm-picker=""
      data-fm-nokeys=""
      onMouseDown={onClose}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "rgba(20,22,35,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fm-card fm-fade relative flex max-h-[80vh] w-full max-w-[520px] flex-col"
        style={{ background: "var(--fm-glass-2)", borderRadius: 28, padding: 20 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[20px] font-semibold tracking-tight">{title}</h2>
            <p className="mt-0.5 text-[13px] text-[color:var(--fm-grey)]">
              {t("mochi.create.picker.note", "Mochi reads them with your access — only files you can open.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="fm-ibtn -me-1 -mt-1"
            aria-label={t("mochi.create.picker.close", "Close")}
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <label className="mt-4 flex h-11 items-center gap-2 rounded-full bg-[#F1F2F6] px-4 focus-within:ring-2 focus-within:ring-[#1F46F4]/60 dark:bg-white/10">
          <Search size={16} className="shrink-0 text-[color:var(--fm-grey)]" aria-hidden />
          <input
            ref={searchRef}
            value={query}
            dir="auto"
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("mochi.create.picker.search", "Search your files")}
            aria-label={t("mochi.create.picker.search", "Search your files")}
            className="min-w-0 flex-1 bg-transparent text-[14px] outline-none"
          />
        </label>
        <h3 className="mb-1 mt-4 px-1 text-[13px] font-semibold text-[color:var(--fm-grey)]">
          {query.trim() ? t("mochi.create.picker.results", "Results") : t("mochi.create.picker.recent", "Recent files")}
        </h3>
        <ul className="min-h-[120px] flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }} aria-label={title}>
          {items === null ? (
            <li className="flex items-center justify-center py-8 text-[color:var(--fm-grey)]">
              <Loader2 className="size-4 animate-spin" aria-label={t("mochi.create.picker.loading", "Loading…")} />
            </li>
          ) : items.length === 0 ? (
            <li className="px-3 py-8 text-center text-[14px] text-[color:var(--fm-grey)]">
              {failed
                ? t("mochi.create.picker.failed", "Files couldn’t be loaded. Try again in a moment.")
                : t("mochi.create.picker.empty", "No files found.")}
            </li>
          ) : (
            items.map((item) => {
              const on = picked.has(item.id);
              return (
                <li key={item.id}>
                  <button type="button" role="checkbox" aria-checked={on} onClick={() => toggle(item)} className="fm-pick-row">
                    <span className="fm-filechip bg-[#F1F2F6] text-[color:var(--fm-grey)] dark:bg-white/10">
                      <FileText size={16} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate" dir="auto">
                      {item.name}
                    </span>
                    {on && <Check size={16} className="text-[color:var(--fm-blue)]" aria-hidden />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="mt-4 flex justify-end gap-2 border-t border-black/[0.06] pt-4 dark:border-white/10">
          <button type="button" onClick={onClose} className="fm-pill fm-pill-white">
            {t("mochi.create.picker.cancel", "Cancel")}
          </button>
          <button
            type="button"
            disabled={picked.size === 0}
            onClick={() => onPick([...picked.values()])}
            className="fm-pill fm-pill-blue"
            style={{ padding: "0 20px" }}
          >
            {t("mochi.create.picker.attach", { defaultValue: "Attach {{count}}", count: picked.size })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
